import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import { AppPathsService } from "../../services/app.paths.service";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25MoreMenuItemComponent } from "../s25-nav-bar/more-menu/s25.more.menu.item.component";
import { S25ClickOutsideDirective } from "../s25-click-outside/s25.click.outside.directive";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25CollapseChevronComponent } from "../s25-collapse-chevron/s25.collapse.chevron.component";
import { LoginStore } from "../../stores/login.store";
import { S25States } from "../../services/state/state.map";
import { AcademicLabels } from "./academic.labels";
import { AcademicSidebarStore } from "./academic.sidebar.store";

@TypeManagerDecorator("s25-ng-academic-top-banner")
@Component({
    selector: "s25-ng-academic-top-banner",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25RouterLinkDirective,
        S25IconComponent,
        S25MoreMenuItemComponent,
        S25ClickOutsideDirective,
        S25IconButtonComponent,
        S25CollapseChevronComponent,
    ],
    host: {
        class: "academic-top-banner",
        "(document:keydown.escape)": "onEscape()",
    },
    template: `
        <div class="academic-top-banner__stack">
            <div class="academic-top-banner__inner">
                <a
                    s25-ng-router-link
                    [stateName]="'academicCurriculum'"
                    class="academic-top-banner__brand"
                    [attr.aria-label]="labels.PRODUCT + ' home'"
                >
                    {{ labels.PRODUCT }}
                </a>
                <div class="academic-top-banner__tools">
                    <button
                        #menuTrigger
                        type="button"
                        class="academic-top-banner__menu c-button c-button--flat"
                        (click)="toggleMenu($event)"
                        [attr.aria-expanded]="menuOpen()"
                        aria-controls="academic-menu-panel"
                        aria-haspopup="menu"
                    >
                        <s25-ng-icon [type]="'menu'" [ariaLabel]="'Menu'" />
                        <span class="academic-top-banner__menu-label" aria-hidden="true">Menu</span>
                    </button>
                    @if (menuOpen()) {
                        <div
                            id="academic-menu-panel"
                            class="academic-menu"
                            role="menu"
                            s25-ng-click-outside
                            (clickOutside)="closeMenu()"
                        >
                            <div class="academic-menu__head">
                                <span class="academic-menu__title">Menu</span>
                                <s25-ng-icon-button
                                    class="academic-menu__close"
                                    [type]="'close'"
                                    [size]="15"
                                    [ariaLabel]="'Close menu'"
                                    (clicked)="closeMenu()"
                                />
                            </div>
                            @if (loginStore.loggedIn() && otherTools().length) {
                                <div class="academic-menu__section">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="academic-menu__section-label academic-menu__section-toggle"
                                        [attr.aria-expanded]="goToToolsExpanded()"
                                        aria-haspopup="true"
                                        (click)="goToToolsExpanded.set(!goToToolsExpanded())"
                                    >
                                        Go to Tool
                                        <s25-ng-collapse-chevron
                                            [collapsedOrientation]="'right'"
                                            [expanded]="goToToolsExpanded()"
                                        />
                                    </button>
                                    @if (goToToolsExpanded()) {
                                        @for (item of otherTools(); track item.displayName) {
                                            <div class="academic-menu__row--button" role="menuitem">
                                                <s25-ng-more-menu-item [item]="item" (itemClicked)="closeMenu()" />
                                            </div>
                                        }
                                    }
                                </div>
                            }
                            @if (loginStore.loggedIn()) {
                                <div class="academic-menu__section academic-menu__section--account">
                                    <div class="academic-menu__section-label">Account</div>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        class="academic-menu__row--button academic-menu__logout"
                                        (click)="logOut()"
                                    >
                                        <s25-ng-icon
                                            [type]="'signout'"
                                            class="academic-menu__row-icon"
                                            [ariaLabel]="false"
                                        />
                                        <span class="academic-menu__row-label">Log Out</span>
                                    </button>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>
            <div class="academic-toolbar">
                <button
                    type="button"
                    class="academic-toolbar__toggle"
                    [class.academic-toolbar__toggle--open]="sidebarStore.isOpen()"
                    (click)="sidebarStore.toggle()"
                    [attr.aria-expanded]="sidebarStore.isOpen()"
                    aria-controls="academic-sidebar"
                >
                    <s25-ng-icon [type]="'menuRightArrow'" [size]="24" [ariaLabel]="'Menu'" />
                    <span aria-hidden="true">Menu</span>
                </button>
            </div>
        </div>
    `,
    styles: `
        :host {
            display: block;
            width: 100%;
        }

        .academic-top-banner__stack {
            position: relative;
            z-index: 100;
        }

        .academic-top-banner__inner {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1.5rem;
            padding: 0.875rem 1.25rem;
            background: var(--color--surface-bg);
            color: var(--color--text);
            border-bottom: 1px solid var(--color--border);
        }

        .academic-top-banner__brand {
            min-width: 0;
            color: inherit;
            font-family: var(--font--header), sans-serif;
            font-size: 1.25rem;
            font-weight: 600;
            text-decoration: none;
        }

        .academic-top-banner__brand:hover,
        .academic-top-banner__brand:focus {
            text-decoration: none;
        }

        .academic-top-banner__tools {
            position: relative;
        }

        .academic-top-banner__menu {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
        }

        .academic-menu {
            position: absolute;
            right: 0;
            top: calc(100% + 8px);
            z-index: 21;
            min-width: 260px;
            max-width: 320px;
            padding: 6px 0;
            background: var(--color--surface-bg);
            color: var(--color--text-strong);
            border: 1px solid var(--color--border);
            border-radius: 10px;
            box-shadow: var(--shadow--dropdown);
        }

        .academic-menu__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 14px 6px;
        }

        .academic-menu__title {
            font-weight: 600;
            letter-spacing: 0.01em;
        }

        .academic-menu__close,
        :host ::ng-deep .academic-menu__close svg {
            color: var(--color--text-muted);
        }

        .academic-menu__close:hover {
            color: var(--color--text-strong);
        }

        .academic-menu__section {
            padding: 4px 0 6px;
        }

        .academic-menu__section + .academic-menu__section {
            border-top: 1px solid var(--color--border);
            margin-top: 4px;
            padding-top: 8px;
        }

        .academic-menu__section-label {
            padding: 6px 14px 4px;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .academic-menu__section-label s25-ng-collapse-chevron {
            float: right;
        }

        .academic-menu__section-toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: transparent;
            border: 0;
            cursor: pointer;
        }

        .academic-menu__section-toggle:hover {
            background: var(--color--surface-bg-hover);
        }

        .academic-menu__section-toggle:focus-visible {
            outline: none;
            background: var(--color--surface-bg-hover);
            box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--color--link-text) 25%, transparent);
        }

        .academic-menu__row--button {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 5px 14px;
            background: transparent;
            border: 0;
            text-align: left;
            font: inherit;
            font-size: 13.5px;
            color: var(--color--text-strong);
            cursor: pointer;
            transition:
                background-color var(--transition-short) ease,
                color var(--transition-short) ease;
        }

        .academic-menu__row--button:hover {
            background: var(--color--surface-bg-hover);
        }

        .academic-menu__row--button:focus-visible,
        [role="menuitem"]:focus-visible {
            outline: none;
            background: var(--color--surface-bg-hover);
            box-shadow: inset 0 0 0 2px color-mix(in srgb, var(--color--link-text) 25%, transparent);
        }

        :host ::ng-deep .academic-menu__row--button s25-ng-more-menu-item,
        :host ::ng-deep .academic-menu__row--button s25-ng-more-menu-item a {
            display: block;
            width: 100%;
            color: var(--color--link-text);
            text-decoration: none;
        }

        :host ::ng-deep .academic-menu__row--button s25-ng-more-menu-item a:hover,
        :host ::ng-deep .academic-menu__row--button s25-ng-more-menu-item a:focus-visible {
            text-decoration: none;
            color: var(--color--link-text-hover);
        }

        .academic-menu__row-icon {
            color: var(--color--text-muted);
            flex-shrink: 0;
        }

        .academic-menu__row-label {
            flex: 1;
            min-width: 0;
        }

        .academic-menu__logout:hover,
        .academic-menu__logout:focus-visible {
            background: var(--color--surface-bg-error);
            color: var(--color--danger);
        }

        .academic-menu__logout:hover .academic-menu__row-icon,
        .academic-menu__logout:focus-visible .academic-menu__row-icon {
            color: var(--color--danger);
        }

        .academic-menu__section--account {
            padding-bottom: 4px;
        }

        .academic-toolbar {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.625rem 1.25rem;
            background: var(--color--surface-2-bg);
            color: var(--color--text);
            border-bottom: 1px solid var(--color--border);
        }

        .academic-toolbar__toggle {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            flex-shrink: 0;
            background: none;
            border: none;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            cursor: pointer;
            color: var(--color--text-muted);
            font-size: 0.875rem;
            font-weight: 500;
            transition:
                color var(--transition-short) ease,
                background-color var(--transition-short) ease;
        }

        .academic-toolbar__toggle:hover {
            color: var(--color--text-strong);
            background-color: var(--color--surface-bg-hover);
        }

        .academic-toolbar__toggle--open s25-ng-icon {
            --icon--rotation-degrees: 180deg;
        }
    `,
})
export class AcademicTopBannerComponent {
    readonly labels = AcademicLabels;
    readonly menuOpen = signal(false);
    readonly goToToolsExpanded = signal(false);
    readonly loginStore = inject(LoginStore);
    readonly sidebarStore = inject(AcademicSidebarStore);
    private readonly menuTrigger = viewChild<ElementRef<HTMLButtonElement>>("menuTrigger");

    private readonly tools = s25Resource({
        loader: () => AppPathsService.getGoToToolMenuItems(),
    });

    readonly otherTools = computed(() => {
        const items = this.tools.value() ?? [];
        return items.filter((item) => item.state?.name !== S25States.names.academicHome && !item.isHidden);
    });

    toggleMenu(event?: Event): void {
        event?.stopPropagation();
        this.menuOpen.update((open) => !open);
    }

    closeMenu(): void {
        this.menuOpen.set(false);
        this.menuTrigger()?.nativeElement.focus();
    }

    onEscape(): void {
        if (this.menuOpen()) this.closeMenu();
    }

    logOut(): void {
        this.closeMenu();
        void this.loginStore.logout(S25States.names.academicHome);
    }
}
